import React, { createContext, useContext, useState, useEffect } from "react";

const ShoppingContext = createContext();
const normalizeCartItem = (item = {}) => ({
  ...item,
  id: item.id || item._id,
  price: typeof (item.price ?? item.mrp ?? item.baseSellingPrice) === "number"
    ? (item.price ?? item.mrp ?? item.baseSellingPrice)
    : parseFloat(String(item.price ?? item.mrp ?? item.baseSellingPrice ?? 0).replace(/₹/g, "").replace(/,/g, "")) || 0,
  quantity: Math.max(1, Number(item.quantity) || 1),
  image: item.image || item.images?.[0],
});

export const ShoppingProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    try { return savedCart ? JSON.parse(savedCart).map(normalizeCartItem) : []; } catch { return []; }
  });

  const [wishlist, setWishlist] = useState(() => {
    const savedWishlist = localStorage.getItem("wishlist");
    return savedWishlist ? JSON.parse(savedWishlist) : [];
  });

  // Persist cart and wishlist to localStorage
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem("wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = (product) => {
    const cartProduct = {
      ...product,
      id: product.id || product._id,
      price: Number(product.price ?? product.mrp ?? product.baseSellingPrice ?? 0),
      image: product.image || product.images?.[0],
      quantity: 1,
    };
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === cartProduct.id);
      if (existingItem) {
        return prevCart.map((item) =>
          item.id === cartProduct.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, cartProduct];
    });
  };

  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
  };

  const updateCartQuantity = (productId, quantity) => {
    setCart((prevCart) =>
      prevCart
        .map((item) =>
          item.id === productId
            ? { ...item, quantity: Math.max(0, quantity) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const toggleWishlist = (product) => {
    setWishlist((prevWishlist) => {
      const exists = prevWishlist.some((item) => item.id === product.id);
      if (exists) {
        return prevWishlist.filter((item) => item.id !== product.id);
      }
      return [...prevWishlist, product];
    });
  };

  const isInWishlist = (productId) => {
    return wishlist.some((item) => item.id === productId);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const rawPrice = item.price ?? item.mrp ?? item.baseSellingPrice ?? 0;
      const price = typeof rawPrice === "number" ? rawPrice : parseFloat(String(rawPrice).replace(/₹/g, "").replace(/,/g, "")) || 0;
      return total + price * item.quantity;
    }, 0);
  };

  const clearCart = () => setCart([]);

  return (
    <ShoppingContext.Provider
      value={{
        cart,
        wishlist,
        addToCart,
        removeFromCart,
        updateCartQuantity,
        toggleWishlist,
        isInWishlist,
        getCartTotal,
        clearCart,
      }}
    >
      {children}
    </ShoppingContext.Provider>
  );
};

export const useShopping = () => {
  const context = useContext(ShoppingContext);
  if (!context) {
    throw new Error("useShopping must be used within a ShoppingProvider");
  }
  return context;
};
